import { ItemsCommand } from './ItemsCommand.js';
import { resolveItemsController } from './ItemsCmdController.js';
import { parseSearchMask, findBySearchMask } from '../imcmd/SearchMask.js';
import { DEFAULT_NS, executeOnItems } from '../imcmd/types.js';

// src/core/imcmd/ItemsUiCommandProvider.ts
/**
 * Провайдер UI-команд для пространства имён "items".
 * Поддерживает только команды из enum ItemsCommand.
 * Задача: подготовить targetItems (если нужно) и делегировать вызов
 * в контроллер элемента: widget.items (без собственной бизнес-логики).
 */
class ItemsUiCommandProvider {
    constructor(opts) {
        this.namespace = opts?.namespace ?? 'items';
        this.priority = opts?.priority ?? 10;
        this.defaultNSAllowed = opts?.defaultNSAllowed ?? true;
        this.id = opts?.id ?? 'items';
    }
    /**
     * Исполнение команды:
     *  1) Валидируем namespace/команду.
     *  2) Если нет targetItems — пытаемся получить из rawArgs[0] (маска) или из targetMask.itemKey.
     *  3) Готовим argv для контроллера: index/indexes из solvedArgs + «хвост» rawArgs.
     *  4) Если целей нет — делегируем на ближайший контроллер относительно sourceEl (без адресата).
     *  5) Если цели есть — обрабатываем каждую через executeOnItems.
     */
    execute(token, ctx) {
        if (token.namespace !== this.namespace && token.namespace !== DEFAULT_NS)
            return false;
        const key = token.command;
        if (!isKnownItemsCommand(key))
            return false;
        let cmdArgs = [];
        if (token.solvedArgs) {
            // TODO: дозаполнить cmdArgs по известным ключам
            const index = token.solvedArgs['index'];
            const indexes = token.solvedArgs['indexes'];
            // либо один индекс, либо массив индексов — это первый аргумент метода
            if (index != null)
                cmdArgs.push(index);
            else if (indexes != null) {
                if (typeof indexes === 'string') {
                    const parts = indexes
                        .split(',')
                        .map(p => p.trim())
                        .filter(p => p.length > 0);
                    cmdArgs.push(parts);
                }
                else {
                    cmdArgs.push(indexes);
                }
            }
            // Нормализация остальных аргументов по ключу команды
            switch (key) {
                case ItemsCommand.update:
                    // update(id, data)
                    if (token.solvedArgs["data"])
                        cmdArgs.push(token.solvedArgs["data"]);
                    else
                        throw new Error(`[update] Не указан обязательный параметр "data"`);
                    break;
                case ItemsCommand.createFolder:
                    // createFolder(title, parentFolderId?)
                    if (token.solvedArgs["title"])
                        cmdArgs.push(token.solvedArgs["title"]);
                    else
                        throw new Error(`[createFolder] Не указан обязательный параметр "title"`);
                    if (token.solvedArgs["parentFolderId"])
                        cmdArgs.push(token.solvedArgs["parentFolderId"]);
                    break;
                case ItemsCommand.renameFolder:
                    // renameFolder(folderId, title)
                    if (token.solvedArgs["title"])
                        cmdArgs.push(token.solvedArgs["title"]);
                    else
                        throw new Error(`[renameFolder] Не указан обязательный параметр "title"`);
                    break;
                case ItemsCommand.moveItemsToFolder:
                    // moveItemsToFolder(itemIds, targetFolderId)
                    if (token.solvedArgs["targetFolderId"])
                        cmdArgs.push(token.solvedArgs["targetFolderId"]);
                    else
                        throw new Error(`[moveItemsToFolder] Не указан обязательный параметр "targetFolderId"`);
                    break;
            }
        }
        // 2) если целей нет — пробуем извлечь по маске из rawArgs[0]
        if (!token.targetItems || token.targetItems.length === 0) {
            // Проверяем есть ли 1й аргумент, считаем что там маска
            if (token.rawArgs && token.rawArgs.length > 0) {
                const maskArg = token.rawArgs[0];
                const mask = parseSearchMask(maskArg);
                // Если аргументов небыло, то остальные параметры из rawArgs считаем аргументами
                if (cmdArgs.length === 0 && token.rawArgs.length > 1)
                    cmdArgs = token.rawArgs.slice(1);
                token.targetItems = findBySearchMask(ctx.viewHost, mask, ctx.sourceEl, ctx.uiNotMask) || [];
            }
            else if (ctx.sourceEl) {
                // Вызов «к себе»: найдём контроллер относительно sourceEl и делегируем без адресата
                const res = resolveItemsController(ctx.sourceEl);
                if (!res) {
                    // Больше нет сценариев поиска
                    return false;
                }
                return res.items.execute(ItemsCommand[key], cmdArgs);
            }
            else
                // Больше нет сценариев поиска
                return false;
        }
        // 2.1) Адресная форма (@key / @(...)) может иметь «хвост» аргументов,
        //      который нужно передать в контроллер. Для варианта с маской в rawArgs[0]
        //      (items::cmd|@mask) сюда НЕ заходим — там хвост уже обработан выше.
        else if (token.targetMask && cmdArgs.length === 0 && token.rawArgs && token.rawArgs.length > 0) {
            cmdArgs = token.rawArgs.slice(0);
        }
        // Если целевых элементов больше 1 то запрещены групповые команды для удаления, обновления, инсерта
        if (token.targetItems.length > 1 && multiWidgetRestricted.has(key)) {
            throw new Error(`Нельзя выполнять групповые команды для "${key}"`);
        }
        // 3) есть цели — выполняем для каждой
        return executeOnItems(token, ctx, (el) => {
            const res = resolveItemsController(el);
            if (!res || !res.items)
                return false;
            return res.items.execute(ItemsCommand[key], cmdArgs);
        });
    }
}
/** Проверяет, что имя команды существует в enum ItemsCommand */
function isKnownItemsCommand(cmd) {
    return Object.prototype.hasOwnProperty.call(ItemsCommand, cmd);
}
/** Список команд, запрещённых в мультирежиме (для нескольких виджетов сразу). */
const multiWidgetRestricted = new Set([
    ItemsCommand.insert,
    ItemsCommand.update,
    ItemsCommand.deleteSelected,
    ItemsCommand.delete,
    ItemsCommand.delMark,
    ItemsCommand.deleteFolder,
    ItemsCommand.createFolder,
    ItemsCommand.renameFolder,
    ItemsCommand.moveUp,
    ItemsCommand.moveDown,
    ItemsCommand.setOrder,
]);

export { ItemsUiCommandProvider };
